//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import ActionButton from '@components/atoms/action-button/ActionButton';
import Icon from '@components/atoms/icon/Icon';
import { BodyBoldText, BodyText } from '@components/atoms/text/Text';
import { SmallTooltipForIcon } from '@components/molecules/tooltip/Tooltip';
import { isDefined, isNotNull } from '@utils/filterPredicates';
import { OnboardingButton } from '@welcome-page/components/onboarding/buttons/OnboardingButton';
import {
  ONBOARDING_STEP_ICON_SIZE,
  OnboardingStepStatus,
} from '@welcome-page/constants/OnboardingConstants';

interface Props {
  description: string;
  pendingMessage?: string;
  icons: React.ReactNode[];
  onClickConnect: () => void;
  onClickSettings?: () => void;
  showAlarmTooltip: boolean;
  status: OnboardingStepStatus;
  title: string;
}

export const OnboardingStepCard = ({
  description,
  icons,
  onClickConnect,
  onClickSettings,
  pendingMessage,
  showAlarmTooltip,
  status,
  title,
}: Props) => {
  const titleToUse: string = (() => {
    switch (status) {
      case OnboardingStepStatus.COMPLETE:
        return `${title} Connected!`;
      case OnboardingStepStatus.INCOMPLETE:
        return title;
      case OnboardingStepStatus.PENDING:
        return `${title} Connection In Progress`;
    }
  })();

  const iconsToUse: React.ReactNode | React.ReactNode[] = (() => {
    switch (status) {
      case OnboardingStepStatus.COMPLETE:
        return <CompleteIcon />;
      case OnboardingStepStatus.INCOMPLETE:
        return icons;
      case OnboardingStepStatus.PENDING:
        return <PendingIcon />;
    }
  })();

  const descriptionToUse: string | undefined = (() => {
    switch (status) {
      case OnboardingStepStatus.COMPLETE:
        return undefined;
      case OnboardingStepStatus.INCOMPLETE:
        return description;
      case OnboardingStepStatus.PENDING:
        return pendingMessage;
    }
  })();

  return (
    <CardContainer status={status}>
      {showAlarmTooltip && (
        <AlarmWrapper>
          <SmallTooltipForIcon
            content={
              <>
                Please contact Bazaarvoice Support if you're having trouble
                getting your account connected.
              </>
            }
            placement='bottom'
            target={<AlarmIcon />}
          />
        </AlarmWrapper>
      )}
      {!!onClickSettings && status === OnboardingStepStatus.INCOMPLETE && (
        <SettingsContainer>
          <ActionButton iconName='settings' onClick={onClickSettings} />
        </SettingsContainer>
      )}
      <ContentContainer>
        <TitleContainer>
          <IconsContainer>{iconsToUse}</IconsContainer>
          <BodyBoldText>{titleToUse}</BodyBoldText>
        </TitleContainer>
        {isDefined(descriptionToUse) &&
          isNotNull(descriptionToUse) &&
          descriptionToUse.length > 0 && (
            <BodyText>{descriptionToUse}</BodyText>
          )}
      </ContentContainer>
      {status === OnboardingStepStatus.INCOMPLETE && (
        <OnboardingButton onClick={onClickConnect}>Connect</OnboardingButton>
      )}
    </CardContainer>
  );
};

const CardContainer = styled.div<{ status: OnboardingStepStatus }>(
  ({ status, theme }) => `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${status === OnboardingStepStatus.INCOMPLETE ? 'space-between' : 'center'};
  position: relative;
  text-align: center;
  padding-top: ${status === OnboardingStepStatus.INCOMPLETE ? theme.spacings[6] : theme.spacings[5]};
  padding-bottom: ${theme.spacings[5]};
  padding-left: ${theme.spacings[3]};
  padding-right: ${theme.spacings[3]};
  background-color: ${theme.colors.white};
  box-shadow: ${theme.boxShadows[2]};
  
  ${
    status === OnboardingStepStatus.COMPLETE &&
    `
      border-top: ${theme.borderWidths[3]} solid ${theme.colors.aliases.success};
    `
  }
`,
);

const ContentContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${theme.spacings[2]};
`,
);

const TitleContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacings[3]};
`,
);

const IconsContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  gap: ${theme.spacings[3]};
`,
);

const CompleteIcon = styled(Icon).attrs({
  name: 'circle-checkmark',
})(
  ({ theme }) => `
  color: ${theme.colors.aliases.success};
  width: ${ONBOARDING_STEP_ICON_SIZE};
  height: ${ONBOARDING_STEP_ICON_SIZE};
`,
);

const PendingIcon = styled(Icon).attrs({
  name: 'more',
})`
  width: ${ONBOARDING_STEP_ICON_SIZE};
  height: ${ONBOARDING_STEP_ICON_SIZE};
`;

const AlarmWrapper = styled.div(
  ({ theme }) => `
  position: absolute;
  top: ${theme.spacings[3]};
  left: ${theme.spacings[3]};
`,
);

const AlarmIcon = styled(Icon).attrs({ name: 'alarm' })(
  ({ theme }) => `
  margin: ${theme.spacings[2]};
  color: ${theme.colors.aliases.warning};
`,
);

const SettingsContainer = styled.div(
  ({ theme }) => `
  position: absolute;
  top: ${theme.spacings[3]};
  right: ${theme.spacings[3]};
`,
);
